import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getBusiness,
  getOrCreateSession,
  saveStep,
  completeSetup,
  updateBusiness,
} from './BusinessService';
import {
  UserBusinessResponse,
  BusinessSetupSessionResponse,
} from '../Financial/types';

const BusinessSetupWizard = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const businessId = Number(id);
  const navigate = useNavigate();

  const [session, setSession] = useState<BusinessSetupSessionResponse | null>(
    null,
  );
  const [business, setBusiness] = useState<UserBusinessResponse | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Profile fields (first step)
  const [profileName, setProfileName] = useState('');
  const [profileIndustry, setProfileIndustry] = useState('');
  const [profileDescription, setProfileDescription] = useState('');

  useEffect(() => {
    Promise.all([
      getBusiness(businessId),
      getOrCreateSession(businessId),
    ])
      .then(([bRes, sRes]) => {
        const biz = bRes.data;
        const ses = sRes.data;
        setBusiness(biz);
        setSession(ses);

        if (ses.status === 'COMPLETED') {
          navigate(`/user/business/${businessId}`);
          return;
        }

        setActiveStep(ses.currentSectionIndex);
        setAnswers(ses.answers ?? {});
        setProfileName(biz.businessName ?? '');
        setProfileIndustry(biz.industry ?? '');
        setProfileDescription(biz.description ?? '');
      })
      .catch(() => setError('Failed to load setup wizard.'))
      .finally(() => setLoading(false));
  }, [businessId, navigate]);

  if (loading || !session || !business) {
    return (
      <UserDashboard>
        <Container>
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        </Container>
      </UserDashboard>
    );
  }

  const sections = session.sections;
  const totalSections = sections.length;
  const progressValue = (activeStep / totalSections) * 100;
  const isReviewStep = activeStep >= totalSections;
  const currentSection = sections[activeStep] ?? null;
  const isProfileStep =
    currentSection?.sectionType === 'PROFILE' || activeStep === 0;

  async function handleNext() {
    if (isReviewStep) return;
    setSaving(true);
    setError(null);

    try {
      // Save profile fields if this is the profile step
      if (isProfileStep) {
        await updateBusiness(businessId, {
          businessName: profileName,
          industry: profileIndustry,
          description: profileDescription,
        });
      }

      const sectionTitle = currentSection?.title ?? '';
      const answer = answers[sectionTitle] ?? '';

      const res = await saveStep(businessId, {
        sectionIndex: activeStep,
        answer,
      });

      setSession(res.data);
      setAnswers(res.data.answers ?? {});
      setActiveStep((prev) => prev + 1);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete() {
    setSaving(true);
    setError(null);
    try {
      await completeSetup(businessId);
      navigate(`/user/business/${businessId}`);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        'Could not complete setup. Ensure all required sections are answered.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  function handleAnswerChange(title: string, value: string) {
    setAnswers((prev) => ({ ...prev, [title]: value }));
  }

  return (
    <UserDashboard>
      <Container>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700}>
            {business.businessName} — Setup
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ mb: 4, borderRadius: 1, height: 6 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {sections.map((s, i) => (
            <Step key={i}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
          <Step key="review">
            <StepLabel>Review</StepLabel>
          </Step>
        </Stepper>

        <Paper variant="outlined" sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
          {isReviewStep ? (
            /* ── Review step ── */
            <>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Review Your Answers
              </Typography>
              <Table size="small">
                <TableBody>
                  {sections.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                        {s.title}
                      </TableCell>
                      <TableCell>
                        {answers[s.title] || (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                          >
                            No answer provided
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box mt={4} display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(totalSections - 1)}
                  disabled={saving}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={saving}
                >
                  {saving ? 'Completing…' : 'Complete Setup'}
                </Button>
              </Box>
            </>
          ) : (
            /* ── Regular section step ── */
            <>
              <Typography variant="h6" fontWeight={600} mb={0.5}>
                {currentSection?.title}
              </Typography>
              {currentSection?.description && (
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {currentSection.description}
                </Typography>
              )}

              {isProfileStep && (
                <Box mb={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Business Name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Industry"
                    value={profileIndustry}
                    onChange={(e) => setProfileIndustry(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    rows={3}
                    value={profileDescription}
                    onChange={(e) => setProfileDescription(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  currentSection?.required
                    ? `${currentSection?.title} (required)`
                    : currentSection?.title
                }
                value={answers[currentSection?.title ?? ''] ?? ''}
                onChange={(e) =>
                  handleAnswerChange(currentSection?.title ?? '', e.target.value)
                }
                sx={{ mb: 3 }}
              />

              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  disabled={activeStep === 0 || saving}
                  onClick={() => setActiveStep((prev) => prev - 1)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving}
                >
                  {saving
                    ? 'Saving…'
                    : activeStep < totalSections - 1
                      ? 'Next'
                      : 'Review'}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </UserDashboard>
  );
};

export default BusinessSetupWizard;
